/**
 * Admin shell layout. Renders sidebar on desktop, hamburger top bar on mobile.
 * Children render in the main pane via <Outlet/>.
 *
 * Visual language: denser, gray slate background, white panels.
 * Brand: small "ShutterKART • Admin" wordmark; gold accent used sparingly.
 */
import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  LayoutDashboard,
  LogOut,
  Menu,
  PieChart,
  Users as UsersIcon,
  UserCog,
  X,
} from "lucide-react";

import { useAdminAuth } from "../../lib/admin-auth";

const NAV_LINKS = [
  { to: "/admin/quotes", label: "Quote queue", icon: LayoutDashboard, roles: null },
  { to: "/admin/talent", label: "Talent", icon: UsersIcon, roles: ["super_admin", "operations"] },
  { to: "/admin/users", label: "Users & roles", icon: UserCog, roles: ["super_admin"] },
  { to: "/admin/cms", label: "CMS", icon: ImageIcon, roles: ["super_admin", "content"] },
  { to: "/admin/reports", label: "Reports", icon: PieChart, roles: ["super_admin", "finance", "operations"] },
  { to: "/admin/audit-logs", label: "Audit logs", icon: FileText, roles: ["super_admin"] },
];

export default function AdminLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { admin, signOut } = useAdminAuth();
  const navigate = useNavigate();

  const roleAllowed = (roles) => {
    if (!roles) return true;
    if (!admin) return false;
    if (admin.role === "super_admin") return true;
    return roles.includes(admin.role);
  };

  const onSignOut = async () => {
    await signOut();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-shell" className="min-h-screen bg-[#F3F4F6] text-[#0F172A]">
      {/* Top bar (mobile + tablet) */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#0F172A]/10 bg-white px-4 py-3 lg:hidden">
        <button
          type="button"
          data-testid="admin-drawer-toggle"
          onClick={() => setDrawerOpen(true)}
          className="rounded-md p-1.5 text-[#0F172A]/70 hover:bg-[#0F172A]/5"
          aria-label="Open admin menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <BrandMark />
        <div className="w-8" />
      </header>

      <div className="mx-auto flex max-w-[1480px]">
        {/* Sidebar (desktop) */}
        <aside
          data-testid="admin-sidebar"
          className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-[#0F172A]/10 bg-white px-5 py-6 lg:flex"
        >
          <BrandMark size="lg" />
          <nav className="mt-10 flex-1 space-y-1">
            {NAV_LINKS.filter((l) => roleAllowed(l.roles)).map((l) => (
              <SideNavLink key={l.to} {...l} />
            ))}
          </nav>
          <UserPill admin={admin} onSignOut={onSignOut} />
        </aside>

        {/* Drawer (mobile + tablet) */}
        {drawerOpen ? (
          <div
            data-testid="admin-drawer"
            className="fixed inset-0 z-40 flex lg:hidden"
            onClick={() => setDrawerOpen(false)}
          >
            <div className="absolute inset-0 bg-[#0F172A]/40" />
            <aside
              className="relative ml-auto flex h-full w-[80%] max-w-xs flex-col bg-white p-5 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <BrandMark size="lg" />
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="rounded-md p-1.5 text-[#0F172A]/70 hover:bg-[#0F172A]/5"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="mt-8 flex-1 space-y-1">
                {NAV_LINKS.filter((l) => roleAllowed(l.roles)).map((l) => (
                  <SideNavLink
                    key={l.to}
                    {...l}
                    onClick={() => setDrawerOpen(false)}
                  />
                ))}
              </nav>
              <UserPill admin={admin} onSignOut={onSignOut} />
            </aside>
          </div>
        ) : null}

        {/* Main */}
        <main data-testid="admin-main" className="min-w-0 flex-1 overflow-x-hidden px-4 pb-12 pt-6 sm:px-6 lg:px-10 lg:pt-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function BrandMark({ size = "md" }) {
  return (
    <Link to="/admin/quotes" className="inline-flex items-center gap-2.5">
      <span
        className={[
          "font-display tracking-tight text-[#0F172A]",
          size === "lg" ? "text-xl" : "text-base",
        ].join(" ")}
      >
        ShutterKART
      </span>
      <span className="rounded-full border border-[#C8A24B]/40 bg-[#C8A24B]/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] text-[#9A7A2F]">
        Admin
      </span>
    </Link>
  );
}

function SideNavLink({ to, label, icon: Icon, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`admin-nav-${to.split("/").pop()}`}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition",
          isActive
            ? "bg-[#0F172A] text-white"
            : "text-[#0F172A]/70 hover:bg-[#0F172A]/5 hover:text-[#0F172A]",
        ].join(" ")
      }
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1 truncate">{label}</span>
      <ChevronRight className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-100" />
    </NavLink>
  );
}

function UserPill({ admin, onSignOut }) {
  if (!admin) return null;
  return (
    <div className="mt-6 rounded-xl border border-[#0F172A]/10 bg-[#F3F4F6] px-3 py-3 text-xs">
      <p className="font-medium text-[#0F172A]" data-testid="admin-current-user">{admin.full_name}</p>
      <p className="mt-0.5 truncate text-[#0F172A]/55">{admin.email}</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="rounded-full bg-[#0F172A]/5 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] text-[#0F172A]/65">
          {admin.role?.replaceAll("_", " ")}
        </span>
        <button
          type="button"
          data-testid="admin-signout"
          onClick={onSignOut}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#0F172A]/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-[#0F172A]/70 hover:border-[#0F172A] hover:text-[#0F172A]"
        >
          <LogOut className="h-3 w-3" />
          Sign out
        </button>
      </div>
    </div>
  );
}

export function AdminPageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl text-[#0F172A]">{title}</h1>
        {subtitle ? (
          <p className="mt-1 text-sm text-[#0F172A]/55">{subtitle}</p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export function AdminBackLink({ to, label = "Back" }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#0F172A]/55 hover:text-[#0F172A]"
    >
      <ChevronLeft className="h-3.5 w-3.5" />
      {label}
    </Link>
  );
}
